import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ARABIC_TEXTS_HEX as ARABIC_TEXTS } from '../constants/arabicTexts';
import {
  Menu,
  X,
  Home,
  Users,
  Shield,
  FileText,
  Settings,
  LogOut,
  User,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  Key
} from 'lucide-react';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navigation = [
    { 
      name: ARABIC_TEXTS.DASHBOARD,
      href: '/admin', 
      icon: Home, 
      current: location.pathname === '/admin' 
    },
    { 
      name: ARABIC_TEXTS.USER_MANAGEMENT,
      href: '/admin/users', 
      icon: Users, 
      current: location.pathname === '/admin/users' 
    },
    { 
      name: ARABIC_TEXTS.ADMIN_MANAGEMENT,
      href: '/admin/admins', 
      icon: Shield, 
      current: location.pathname === '/admin/admins',
      roles: ['admin'] // Only visible to admin role
    },
    { 
      name: ARABIC_TEXTS.FORM_MANAGEMENT,
      href: '/admin/prenups', 
      icon: FileText, 
      current: location.pathname === '/admin/prenups' 
    },
    { 
      name: 'سجل المعاملات',
      href: '/admin/audit-logs', 
      icon: ClipboardList, 
      current: location.pathname === '/admin/audit-logs',
      roles: ['admin', 'editor'] // Visible to admin and editor roles
    },
    { 
      name: ARABIC_TEXTS.SETTINGS,
      href: '/admin/settings', 
      icon: Settings, 
      current: location.pathname.startsWith('/admin/settings'),
      roles: ['admin'], // Only visible to admin role
      subItems: [
        {
          name: 'إعدادات عامة',
          href: '/admin/settings',
          current: location.pathname === '/admin/settings'
        },
        {
          name: 'الاستمارات',
          href: '/admin/settings/documents',
          current: location.pathname === '/admin/settings/documents'
        },
        {
          name: 'البريد الإلكتروني (SMTP)',
          href: '/admin/settings/smtp',
          current: location.pathname === '/admin/settings/smtp'
        },
        {
          name: 'تغيير كلمة المرور',
          href: '/admin/settings/change-password',
          current: location.pathname === '/admin/settings/change-password',
          allRoles: true // Available to all admin roles (admin and editor)
        }
      ]
    },
  ].filter(item => {
    // Filter navigation items based on user role
    if (!item.roles || item.roles.length === 0) {
      return true; // No role restriction, show to all
    }
    return item.roles.includes(user?.role);
  });

  const handleLogout = () => {
    logout();
    navigate('/prenup/SystemUser/Login', { replace: true });
  };

  const getCurrentPageName = () => {
    // Check for sub-items first
    for (const item of navigation) {
      if (item.subItems) {
        const currentSubItem = item.subItems.find(subItem => subItem.current);
        if (currentSubItem) {
          return `${item.name} - ${currentSubItem.name}`;
        }
      }
    }
    
    const currentPage = navigation.find(item => item.current);
    return currentPage?.name || ARABIC_TEXTS.DASHBOARD;
  };

  const getUserRoleText = () => {
    return user?.role === 'admin' ? ARABIC_TEXTS.ADMIN : ARABIC_TEXTS.EDITOR;
  };

  const getUserRoleFullText = () => {
    return user?.role === 'admin' ? ARABIC_TEXTS.SYSTEM_ADMIN : ARABIC_TEXTS.EDITOR;
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 right-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:transform-none
        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-4 bg-blue-600">
          <h1 className="text-white text-lg font-semibold">
            {ARABIC_TEXTS.DASHBOARD}
          </h1>
          <button
            className="lg:hidden text-white hover:text-gray-200"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 flex-1">
          <div className="px-2 space-y-1">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isExpanded = hasSubItems && settingsExpanded;
              
              return (
                <div key={index}>
                  <a
                    href={hasSubItems ? undefined : item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      if (hasSubItems) {
                        setSettingsExpanded(!settingsExpanded);
                      } else {
                        navigate(item.href);
                        setSidebarOpen(false);
                      }
                    }}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer
                      ${item.current
                        ? 'bg-blue-100 text-blue-900 border-r-4 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <Icon className={`
                      ml-3 w-5 h-5 transition-colors
                      ${item.current ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}
                    `} />
                    <span className="flex-1">{item.name}</span>
                    {hasSubItems && (
                      <ChevronLeft
                        className={`w-4 h-4 transition-transform ${
                          isExpanded ? '-rotate-90' : ''
                        }`}
                      />
                    )}
                  </a>
                  
                  {hasSubItems && isExpanded && (
                    <div className="mr-8 mt-1 space-y-1">
                      {item.subItems.map((subItem, subIndex) => (
                        <a
                          key={subIndex}
                          href={subItem.href}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(subItem.href);
                            setSidebarOpen(false);
                          }}
                          className={`
                            block px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer
                            ${subItem.current
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }
                          `}
                        >
                          {subItem.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navigation bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4">
            {/* Mobile menu button */}
            <button
              className="lg:hidden text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Page title */}
            <div className="flex-1 flex items-center lg:justify-start justify-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {getCurrentPageName()}
              </h2>
            </div>

            {/* User menu */}
            <div className="relative">
              <button
                className="flex items-center max-w-xs bg-white text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="flex items-center space-x-3 space-x-reverse px-3 py-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="hidden md:block text-right">
                    <p className="text-sm font-medium text-gray-700">{user?.fullName}</p>
                    <p className="text-xs text-gray-500">{getUserRoleText()}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </div>
              </button>

              {/* User dropdown menu */}
              {userMenuOpen && (
                <div className="origin-top-left absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                      <p className="text-xs text-blue-600 mt-1">
                        {getUserRoleFullText()}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/admin/settings/change-password');
                      }}
                      className="w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors"
                    >
                      <Key className="w-4 h-4 ml-2 text-blue-500" />
                      تغيير كلمة المرور
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors"
                    >
                      <LogOut className="w-4 h-4 ml-2 text-red-500" />
                      {ARABIC_TEXTS.LOGOUT}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;